import React, { FC, useEffect, useState } from 'react';

import { BodySmall, Box, Modal, ModalFooter, ModalHeader, Skeleton, TextField } from '@parspec/pixel';

import { useGenerateQRCodeForAuthenticatorMutation, useVerifyAuthenticatorOtpMutation } from 'src/features/Authentication/queries';
import { ACCOUNT_LOCKED_MSG, AUTHENTICATION_SETUP_COMPLETE, INVALID_OTP_MSG, REQUIRED_FIELD } from 'src/features/shared/constants';
import { AUTHY_URL, GOOGLE_AUTHENTICATOR_URL } from '../constant';

interface AuthenticatorModalProps {
    open: boolean;
    onClose: () => void;
    handleSnackBarMsg: (msg: string) => void;
    onClickGenerateCode: () => void;
}

const AuthenticatorModal: FC<AuthenticatorModalProps> = ({ open, onClose, handleSnackBarMsg, onClickGenerateCode }) => {
    const [code, setCode] = useState('');
    const [helperText, setHelperText] = useState('');
    const [qrCodeLink, setQrCodeLink] = useState('');

    const { mutateAsync: generateQrCode, isLoading } = useGenerateQRCodeForAuthenticatorMutation();

    const { mutateAsync: verifyOtp, isLoading: verifyOtpLoading } = useVerifyAuthenticatorOtpMutation();

    const getQrCode = async () => {
        const response = await generateQrCode();
        if (response?.data) {
            const imageObjectURL = URL.createObjectURL(response?.data);
            setQrCodeLink(imageObjectURL);
        }
    };

    useEffect(() => {
        getQrCode();
    }, []);

    const handleChangeCode = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCode(e.target.value);
        setHelperText('');
    };

    const handleClickGoogleAuthenticator = () => {
        window.open(GOOGLE_AUTHENTICATOR_URL);
    };

    const handleClickAuthy = () => {
        window.open(AUTHY_URL);
    };

    const handleSubmit = async () => {
        if (!code.trim()) {
            return setHelperText(REQUIRED_FIELD);
        }
        try {
            await verifyOtp(code);
            handleSnackBarMsg(AUTHENTICATION_SETUP_COMPLETE);
            onClickGenerateCode();
            onClose();
        } catch (e: any) {
            if (e.response.status === 400) {
                setHelperText(INVALID_OTP_MSG);
            }
            if (e.response.status === 423) {
                handleSnackBarMsg(ACCOUNT_LOCKED_MSG);
            }
        }
    };

    return (
        <Modal
            header={<ModalHeader title="Set up Authenticator" onClose={onClose} />}
            footer={<ModalFooter onAccept={handleSubmit} onReject={onClose} continueButtonLabel="Verify" cancelButtonLabel="Back" isLoading={verifyOtpLoading} disabled={isLoading} />}
            open={open}
        >
            <Box width={406} mt={4}>
                <Box>
                    <BodySmall limit={false}>
                        <span style={{ fontWeight: 500 }}>Step one:</span> Install the
                        <Box component="span" ml={1} onClick={handleClickGoogleAuthenticator} sx={{ cursor: 'pointer', color: 'primary.main' }}>
                            Google Authenticator
                        </Box>
                        ,
                        <Box component="span" mx={1} onClick={handleClickAuthy} sx={{ cursor: 'pointer', color: 'primary.main' }}>
                            Authy
                        </Box>
                        or any other compatible MFA app on your mobile device.
                    </BodySmall>
                </Box>
                <Box mt={4}>
                    <BodySmall limit={false}>
                        <span style={{ fontWeight: 500 }}>Step two:</span> Scan QR code with the mobile authenticator app.
                    </BodySmall>
                </Box>

                <Box display="flex" justifyContent="center" mt={2}>
                    {isLoading ? <Skeleton height={120} width={120} variant="rectangular" /> : <img src={qrCodeLink} height={120} width={120} />}
                </Box>

                <Box mt={4} width="70%">
                    <TextField value={code} onChange={handleChangeCode} required label="Authentication Code" helperText={helperText} error={Boolean(helperText)} />
                </Box>
            </Box>
        </Modal>
    );
};

export default AuthenticatorModal;
